import { ApolloError, ApolloQueryResult } from '@apollo/client'
import { useCallback, useMemo, useRef, useState } from 'react'

import {
  CollectionGridSectionFragmentFragment,
  GetCollectionQuery,
  GetCollectionQueryVariables,
} from '../../../../../__generated__/graphql/catalog/graphql'
import { useGraphqlClients } from '../../../../../app/_providers/GraphqlClientsProvider.client'
import {
  COLLECTION_ITEM_MAX_ROW_SIZE,
  COLLECTION_ITEM_PAGE_SIZE,
} from '../../../../../brunswick/constants'
import {
  GET_COLLECTION,
  filterKeysFromFilterConfig,
  sanitizeFilterConfig,
} from '../../../../../lib/graphql/queries/GET_COLLECTION'
import { QueryStatus } from '../../../../../lib/hooks/types'
import { CollectionItemFilterInput } from '../../../../../types/graphql/collection-view'

type FetchMoreQueryStatus = Exclude<QueryStatus, 'pending'> | 'pending-initial' | 'pending-more'

type CollectionQueryOverview = {
  handle: string
  status: FetchMoreQueryStatus
  data: GetCollectionQuery['collection'] | null
  error: ApolloError | null
}

type UseCollectionGridSectionCollectionData = {
  filters: NonNullable<NonNullable<GetCollectionQuery['collection']>['filters']>
  colors: NonNullable<NonNullable<GetCollectionQuery['collection']>['colors']>
  collections: Array<{
    handle: string
    items: NonNullable<NonNullable<GetCollectionQuery['collection']>['items']>
    numItems: number
  }>
  numItems: number | null
}

export type UseCollectionGridSectionCollectionsProps = {
  collectionHandles: string[]
  collectionColorScope: GetCollectionQueryVariables['collectionColorScope']
  filterConfig: CollectionGridSectionFragmentFragment['filterConfig']
  selectedFilters: CollectionItemFilterInput[]
  groupedProductTiles: boolean
}

/**
 * Warning, there be dragons. It is tricky to page not only through a collection, but a list of collections.
 * If you are altering this, you should test:
 * - The collection page loads correctly with JavaScript disabled in the browser (SSR looks right, has images and total count)
 * - The collection page can load multiple collections completely through scrolling
 * - Pages of products loaded via scrolling don't dissapear after hovering a tile
 * - All tiles can be hovered to see their options
 * - Scroll location never slips as you scroll and hover
 * - Filters on the page do not show an option as not available, just because it is not available in one collection, but is available in another collection. Basically, all filter options from all collections should show, and only if all collections say an option is unavailable should it be shown as such.
 * - Filters are merged from all collections, without creating duplicates.
 * - Color filters have access to names and swatches
 * - Total item count should be calculatable instantly for all collections, even ones not paged through yet.
 * - Filtering should change the total item count
 * - Filtering for a category only found in the third collection does not cause odd behavior, we skip loading more from the preceeding collections which have zero items
 */
export const useCollectionGridSectionCollections = ({
  filterConfig: unsanitizedFilterConfig,
  collectionHandles,
  collectionColorScope,
  selectedFilters,
  groupedProductTiles,
}: UseCollectionGridSectionCollectionsProps) => {
  const { cacheOptimizedClient: figsPublicClient } = useGraphqlClients()

  const [filterConfig, filterKeys] = useMemo(() => {
    return [
      sanitizeFilterConfig(unsanitizedFilterConfig),
      filterKeysFromFilterConfig(unsanitizedFilterConfig),
    ]
  }, [unsanitizedFilterConfig])

  // Initialize the array of results for all provided collection handles to query
  const previousBaseCollectionQueries = useRef<CollectionQueryOverview[] | undefined>(undefined)
  const baseCollectionQueries = useMemo(() => {
    return collectionHandles.map((collectionHandle, index) => {
      const cachedResult = figsPublicClient.readQuery({
        query: GET_COLLECTION,
        variables: {
          collectionHandle: collectionHandle,
          collectionColorScope,
          filterConfig,
          filterKeys,
          selectedFilters,
          nextPageDataOnly: false,
          first: index === 0 ? COLLECTION_ITEM_PAGE_SIZE : COLLECTION_ITEM_MAX_ROW_SIZE, // Loading one row helps a lot for the case where we filter to a filter option only found in a later collection than the initial one. That way we see some products right away and not just a page loading indication
          grouping: groupedProductTiles ? 'PRODUCT_GROUP' : 'PRODUCT',
        },
      })
      const cachedData = cachedResult?.collection
      const collectionQueryOverview: CollectionQueryOverview = {
        handle: collectionHandle,
        status: cachedData ? 'resolved' : 'idle',
        data: cachedData ?? null,
        error: null,
      }
      return collectionQueryOverview
    })
  }, [
    collectionHandles,
    collectionColorScope,
    figsPublicClient,
    filterConfig,
    filterKeys,
    groupedProductTiles,
    selectedFilters,
  ])

  // This is some React specific magic. Basically, when someone requests new information from this hook,
  // we get a new baseCollectionQueries result. When we have a new baseline, we need to replace our
  // actual state that everything relies on to trigger a series of reactivity. We can't just leverage useMemo,
  // because our query results are not only based on inputs, they also grow via fetchMore. And we can't only
  // leverage useState, because then new inputs for selected filters or collectionHandles wouldn't cause fresh data.
  const [collectionQueries, setCollectionQueries] = useState(baseCollectionQueries)
  if (previousBaseCollectionQueries.current !== baseCollectionQueries) {
    // Since we can't use `useQuery` an arbitrary amount of times, we need to trigger queries for all collection handles that did not find data from the Apollo cache that should have been primed on the page in getInitialProps
    baseCollectionQueries.forEach((baseCollectionQuery, index) => {
      if (baseCollectionQuery.status === 'idle') {
        baseCollectionQuery.status = 'pending-initial'
        figsPublicClient
          .query({
            query: GET_COLLECTION,
            variables: {
              collectionHandle: baseCollectionQuery.handle,
              collectionColorScope,
              filterConfig,
              filterKeys,
              selectedFilters,
              nextPageDataOnly: false,
              first: index === 0 ? COLLECTION_ITEM_PAGE_SIZE : COLLECTION_ITEM_MAX_ROW_SIZE, // Loading one row helps a lot for the case where we filter to a filter option only found in a later collection than the initial one. That way we see some products right away and not just a page loading indication
              grouping: groupedProductTiles ? 'PRODUCT_GROUP' : 'PRODUCT',
            },
          })
          .then(results => {
            handleCollectionQueryResponse({
              collectionHandle: baseCollectionQuery.handle,
              newQueryResults: results,
            })
          })
      }
    })
    if (previousBaseCollectionQueries.current) {
      setCollectionQueries(baseCollectionQueries)
    }
  }
  previousBaseCollectionQueries.current = baseCollectionQueries

  // Function for updating the array of query results with new data
  const handleCollectionQueryResponse = useCallback(
    ({
      collectionHandle,
      newQueryResults,
    }: {
      collectionHandle: string
      newQueryResults: ApolloQueryResult<GetCollectionQuery>
    }) => {
      const currentQueryOverview = collectionQueries.find(
        nextCollectionQuery => nextCollectionQuery.handle === collectionHandle
      )
      if (!currentQueryOverview) return

      const pageDataCollection = newQueryResults.data.collection
      if (newQueryResults.error || !pageDataCollection) {
        currentQueryOverview.error = newQueryResults.error ?? null
        currentQueryOverview.status = 'rejected'
      } else {
        currentQueryOverview.status = 'resolved'
        currentQueryOverview.data = {
          ...currentQueryOverview.data,
          ...pageDataCollection,
        }
      }

      setCollectionQueries([...collectionQueries])
    },
    [collectionQueries]
  )

  // Reduce/merge down to a single status
  const status = useMemo<FetchMoreQueryStatus>(() => {
    const statuses = collectionQueries.map(collectionQuery => collectionQuery.status)

    return statuses.reduce<FetchMoreQueryStatus>((overallStatus, nextStatus) => {
      if (nextStatus === 'pending-initial' || overallStatus === 'pending-initial')
        return 'pending-initial'
      if (nextStatus === 'pending-more' || overallStatus === 'pending-more') return 'pending-more'
      if (nextStatus === 'rejected' || overallStatus === 'rejected') return 'rejected'
      if (nextStatus === 'resolved' || overallStatus === 'resolved') return 'resolved'
      return 'idle'
    }, 'idle')
  }, [collectionQueries])

  // Reduce/merge down to a single data output
  const data = useMemo(() => {
    let hitCollectionWithMoreItemsToLoad = false
    const data = collectionQueries.reduce<UseCollectionGridSectionCollectionData>(
      (overallData, nextCollectionQuery) => {
        // Merge colors
        const nextCollectionColors = nextCollectionQuery.data?.colors
        if (nextCollectionColors) {
          nextCollectionColors.forEach(nextColor => {
            const matchingColor = overallData.colors.find(color => color.name === nextColor.name)
            if (!matchingColor) {
              overallData.colors.push(nextColor)
            }
          })
        }

        // Merge filters
        const nextCollectionFilters = nextCollectionQuery.data?.filters
        if (nextCollectionFilters) {
          nextCollectionFilters.forEach(nextFilterReadOnly => {
            const nextFilter = JSON.parse(
              JSON.stringify(nextFilterReadOnly)
            ) as typeof nextFilterReadOnly
            const matchingFilter = overallData.filters.find(filter => filter.key === nextFilter.key)
            if (!matchingFilter) {
              overallData.filters.push(nextFilter)
            } else {
              nextFilter.filterValues.forEach(nextFilterValue => {
                const matchingFilterValue = matchingFilter.filterValues.find(
                  filterValue => filterValue.value === nextFilterValue.value
                )
                if (!matchingFilterValue) {
                  matchingFilter.filterValues.push(nextFilterValue)
                } else {
                  matchingFilterValue.state =
                    matchingFilterValue.state === 'DISABLED'
                      ? nextFilterValue.state
                      : matchingFilterValue.state
                }
              })
            }
          })
        }

        // Merge numItems
        overallData.numItems =
          (nextCollectionQuery.data?.items?.numItems ?? 0) + (overallData.numItems ?? 0)

        // Merge items
        // Do not merge items if we haven't paged through all prior collections
        if (hitCollectionWithMoreItemsToLoad === false) {
          const nextCollectionItems = nextCollectionQuery.data?.items
          if (nextCollectionItems) {
            overallData.collections.push({
              handle: nextCollectionQuery.handle,
              items: nextCollectionItems,
              numItems: nextCollectionQuery.data?.items?.numItems ?? 0,
            })

            if (nextCollectionItems.numItems > nextCollectionItems.nodes.length) {
              hitCollectionWithMoreItemsToLoad = true
            }
          }
        }

        return overallData
      },
      {
        filters: [],
        colors: [],
        collections: [],
        numItems: 0,
      }
    )

    // Filters displayed on the PLP could be a combination of filters applicable to multiple collections. This code
    // ensures that the final list of filters still respects the ordering from the Filter Config in Contentful.
    data?.filters?.sort((a, b) => {
      const orderA = filterKeys.indexOf(a.key)
      const orderB = filterKeys.indexOf(b.key)

      if (orderA === undefined && orderB === undefined) return 0
      if (orderA === undefined) return 1
      if (orderB === undefined) return -1

      return orderA - orderB
    })

    return data
  }, [collectionQueries, filterKeys])

  // Reduce/merge down to a single fetchMore for paging through all collections
  const fetchMore = useMemo(() => {
    if (status === 'pending-initial' || status === 'pending-more' || status === 'rejected')
      return undefined

    const collectionToQuery = collectionQueries.find(collectionQuery => {
      const hasData = collectionQuery.data
      const hasNextPage = collectionQuery.data?.items?.pageInfo?.hasNextPage
      const hasItems = collectionQuery.data?.items?.numItems ?? 0 > 0
      return (hasNextPage && hasItems) || !hasData
    })

    if (collectionToQuery) {
      return async () => {
        collectionToQuery.status = 'pending-more'
        const targetFirstPage =
          (collectionToQuery.data?.items?.nodes?.length ?? 0) < COLLECTION_ITEM_PAGE_SIZE

        // Set state so we display in a loading state
        setCollectionQueries([...collectionQueries])

        const results = await figsPublicClient.query({
          query: GET_COLLECTION,
          variables: {
            collectionHandle: collectionToQuery.handle,
            collectionColorScope,
            filterConfig,
            filterKeys,
            selectedFilters,
            nextPageDataOnly: true,
            first: COLLECTION_ITEM_PAGE_SIZE,
            after: targetFirstPage ? undefined : collectionToQuery.data?.items?.pageInfo.endCursor,
            grouping: groupedProductTiles ? 'PRODUCT_GROUP' : 'PRODUCT',
          },
          fetchPolicy: 'network-only',
        })

        handleCollectionQueryResponse({
          collectionHandle: collectionToQuery.handle,
          newQueryResults: results,
        })
      }
    } else {
      return undefined
    }
  }, [
    collectionQueries,
    collectionColorScope,
    figsPublicClient,
    filterConfig,
    filterKeys,
    groupedProductTiles,
    handleCollectionQueryResponse,
    selectedFilters,
    status,
  ])

  return {
    data,
    fetchMore,
    status,
  }
}
